import { FileCogIcon } from 'lucide-react'

import { Button, type ButtonProps } from '@/components/ui'
import type { Table } from '@/components/ui/table/types'
import { useToast } from '@/hooks'
import {
  isDocumentProcessing,
  isDocumentToStart,
} from '@/modules/documents/status'
import { useFolder } from '@/modules/folders'
import { cn } from '@/utils'
import {
  useDocument,
  useDocumentMutations,
  useDocuments,
  useDocumentsMutations,
} from '../hooks'
import type { Document } from '../stores'

interface ProcessButton extends ButtonProps {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => Promise<void>
  children?: React.ReactNode
  icon?: React.ComponentType<{ className?: string }>
}

const ProcessButton = ({
  isLoading,
  disabled,
  children,
  onClick,
  icon: Icon,
  ...props
}: ProcessButton) => {
  const { successToast, errorToast } = useToast()

  const triggerProcess = async (e: React.MouseEvent<HTMLButtonElement>) => {
    try {
      await onClick?.(e)
      successToast('Process documents started')
    } catch (e) {
      const error = e as Error
      const title = 'Failed to process documents'

      errorToast(title, error)
    }
  }

  return (
    <span className={cn('relative')}>
      <Button
        isLoading={isLoading}
        loadingText="Processing..."
        disabled={disabled}
        onClick={triggerProcess}
        {...props}
      >
        {Icon ? <Icon /> : <FileCogIcon />}
        {children}
      </Button>
    </span>
  )
}
interface ProcessDocumentsButton extends ButtonProps {
  table?: Table<Document> | null
  icon?: React.ComponentType<{ className?: string }>
  documentsToProcess?: Document[]
  children?: React.ReactNode
}

export const ProcessDocumentsButton = ({
  table,
  documentsToProcess,
  children,
  onClick,
  icon,
  disabled,
  ...props
}: ProcessDocumentsButton) => {
  const allDocuments = useDocuments()
  const folder = useFolder()
  const { ocrDocuments } = useDocumentsMutations()
  const documents = documentsToProcess || allDocuments

  const hasDocumentsToStart = documents.filter(isDocumentToStart).length > 0
  const hasProcessingDocuments =
    documents.filter(isDocumentProcessing).length > 0

  const triggerProcess = () => {
    table?.resetRowSelection()
    const ids = documentsToProcess
      ? documentsToProcess.map(({ id }) => id)
      : undefined

    return ocrDocuments(folder?.id || null, ids)
  }

  return (
    <ProcessButton
      isLoading={!hasDocumentsToStart && hasProcessingDocuments}
      loadingText="Processing..."
      disabled={!hasDocumentsToStart}
      onClick={async (e) => {
        await triggerProcess()
        onClick?.(e)
      }}
      icon={icon}
      {...props}
    >
      {children}
    </ProcessButton>
  )
}

interface ProcessDocumentButton extends ButtonProps {
  icon?: React.ComponentType<{ className?: string }>
  children?: React.ReactNode
}

export const ProcessDocumentButton = ({
  children,
  onClick,
  icon,
  disabled,
  ...props
}: ProcessDocumentButton) => {
  const document = useDocument()
  const { ocrDocument } = useDocumentMutations()

  const toStart = document ? isDocumentToStart(document) : false
  const isProcessing = document ? isDocumentProcessing(document) : false

  return (
    <ProcessButton
      isLoading={!toStart && isProcessing}
      loadingText="Processing..."
      disabled={!toStart}
      onClick={async (e) => {
        await ocrDocument()
        onClick?.(e)
      }}
      icon={icon}
      {...props}
    >
      {children}
    </ProcessButton>
  )
}
